import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from '../../constants';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useCurrentUser } from '../AuthComponents';
import { useExperiment, useStudentProgress, usePreLabQuestions, useDiscussionQuestions, useSubmitPreLabAssessment, useTeacherBypass, useSaveLabData, useSubmitDiscussionAnswers } from '../../utils/convexHooks';
import { Id } from '../../convex/_generated/dataModel';
import PreLabAssessment from '../PreLabAssessment';
import ExperimentScene3D from './ExperimentScene3D';
import ExperimentProcedure from './ExperimentProcedure';
import ExperimentTable from './ExperimentTable';
import ExperimentTutorial, { TUTORIAL_STEPS } from './ExperimentTutorial';
import SimulationControls from './SimulationControls';
import { useZnH2SO4Store, MAX_EXPERIMENT_TIME } from './store/useZnH2SO4Store';

// ─── Observation messages ─────────────────────────────────────────────────────

const getObservation = (step: number, isReacting: boolean, time: number): string => {
  if (step === 0) return 'Ready to start. Add the sulphuric acid to begin.';
  if (step === 1) return 'H₂SO₄ added to the conical flask. Now set up the inverted burette.';
  if (step === 2) return 'Burette set up with water at 50.00 cm³. Now add the zinc powder.';
  if (step === 3) return 'Zinc powder added. Zinc is a grey, powdery metal. Seal the flask and start the timer!';
  if (step >= 4 && !isReacting && time === 0) return 'Flask sealed. Ready to start the reaction.';
  if (isReacting) {
    if (time < 1.0) return 'Bubbles forming rapidly as Zn reacts with H₂SO₄. Gas collecting in burette!';
    if (time < 2.5) return 'Reaction in progress. Water level dropping as H₂ gas accumulates.';
    if (time < 4.0) return 'Reaction slowing. Fewer bubbles visible. Gas collection rate decreasing.';
    return 'Reaction nearly complete. Very few bubbles. Zinc almost fully consumed.';
  }
  if (time >= MAX_EXPERIMENT_TIME) return 'Experiment complete! Reaction finished — all zinc has reacted.';
  return '';
};

// ─── State ────────────────────────────────────────────────────────────────────

type ActiveTab = 'procedure' | 'data';

const TUTORIAL_SEEN_KEY = 'guidedExperimentTutorialSeen';

// ─── Main Component ────────────────────────────────────────────────────────────

interface ZnH2SO4LabBaseProps {
  experimentConfig: any;
  progress: any;
  discussionQuestions: any;
}

const ZnH2SO4LabBase: React.FC<ZnH2SO4LabBaseProps> = ({ experimentConfig, progress, discussionQuestions }) => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const saveLabData = useSaveLabData();
  const submitDiscussionAnswers = useSubmitDiscussionAnswers();

  // Store subscriptions
  const state = useZnH2SO4Store();

  // Local UI state
  const [showTutorial, setShowTutorial] = React.useState<boolean>(() => {
    if (experimentConfig.showTutorial !== undefined) return experimentConfig.showTutorial;
    return !localStorage.getItem(TUTORIAL_SEEN_KEY);
  });
  const [showAssistant, setShowAssistant] = React.useState<boolean>(() => {
    return experimentConfig.enableAssistant !== undefined ? experimentConfig.enableAssistant : true;
  });
  const [tutorialStep, setTutorialStep] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('procedure');

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tutorial handlers
  const handleTutorialNext = useCallback(() => {
    if (tutorialStep < TUTORIAL_STEPS.length - 1) setTutorialStep((s) => s + 1);
    else { setShowTutorial(false); localStorage.setItem(TUTORIAL_SEEN_KEY, 'true'); }
  }, [tutorialStep]);

  const handleTutorialSkip = useCallback(() => {
    setShowTutorial(false); localStorage.setItem(TUTORIAL_SEEN_KEY, 'true');
  }, []);

  const handleOpenTutorial = useCallback(() => {
    setTutorialStep(0); setShowTutorial(true);
  }, []);

  // Step handler
  const handleStep = useCallback((stepId: number) => {
    if (stepId !== state.currentStep) return;
    if (stepId === 3) {
      state.startReaction();
      setActiveTab('data');
    } else {
      state.setCurrentStep(stepId + 1);
    }
  }, [state.currentStep, state]);

  // Reaction timer — respects pause and playback speed
  useEffect(() => {
    if (!state.isReacting || state.isPaused) return;
    const tickInterval = 100;
    const baseMinutesPerTick = MAX_EXPERIMENT_TIME / (30 * (1000 / tickInterval));
    const minutesPerTick = baseMinutesPerTick * state.playbackSpeed;

    intervalRef.current = setInterval(() => {
      state.tick(minutesPerTick);
    }, tickInterval);

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [state.isReacting, state.isPaused, state.playbackSpeed, state]);

  useEffect(() => {
    if (state.highlightTime !== null) {
      const t = setTimeout(() => state.setHighlightTime(null), 2000);
      return () => clearTimeout(t);
    }
  }, [state.highlightTime, state]);

  // Reset
  const handleReset = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    state.resetExperiment();
    setActiveTab('procedure');
  }, [state]);

  // Playback controls
  const handlePauseResume = useCallback(() => {
    if (state.experimentComplete && !state.isReacting) {
      state.setPaused(true);
      // Re-enter reacting
      state.setCurrentStep(4);
    } else {
      state.setPaused(!state.isPaused);
    }
  }, [state.experimentComplete, state.isReacting, state]);

  const handleSpeedChange = useCallback((speed: number) => {
    state.setPlaybackSpeed(speed);
  }, [state]);

  const handleSeek = useCallback((targetTime: number) => {
    state.seekTime(targetTime);
  }, [state]);

  // Manual recording from stopwatch
  const handleRecordReading = useCallback((time: number, reading: number) => {
    state.recordReading(time, reading);
  }, [state]);

  const observation = getObservation(state.currentStep, state.isReacting, state.experimentTime);
  const reactionProgress = state.experimentTime / MAX_EXPERIMENT_TIME;
  const overallProgress = Math.min(100, (state.currentStep / 4) * 60 + reactionProgress * 40);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: HOLOLAB_FONTS.body, background: HOLOLAB_COLORS.iceBlue, overflow: 'hidden' }}>
      {showTutorial && <ExperimentTutorial tutorialStep={tutorialStep} onNext={handleTutorialNext} onSkip={handleTutorialSkip} />}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #f8fbfd 100%)', borderBottom: '1px solid rgba(10,25,41,0.06)', padding: '0 20px', height: '48px', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, zIndex: 10, position: 'relative' }}>
        <button
          onClick={() => navigate('/student/dashboard')}
          aria-label="Back"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'transparent', border: '1px solid rgba(10,25,41,0.1)', borderRadius: '6px', padding: '5px 12px', fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 500, color: 'rgba(10,25,41,0.55)', cursor: 'pointer', transition: 'all 150ms', flexShrink: 0 }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = HOLOLAB_COLORS.precisionBlue; e.currentTarget.style.color = HOLOLAB_COLORS.precisionBlue; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(10,25,41,0.1)'; e.currentTarget.style.color = 'rgba(10,25,41,0.55)'; }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back
        </button>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 style={{ fontFamily: HOLOLAB_FONTS.heading, fontSize: '14px', fontWeight: 700, color: HOLOLAB_COLORS.deepBlue, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Experiment A
          </h1>
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', color: 'rgba(10,25,41,0.45)', whiteSpace: 'nowrap' }}>
            Reaction between Zinc and Sulphuric Acid
          </span>
          <span style={{ fontFamily: HOLOLAB_FONTS.mono, fontSize: '11px', fontWeight: 500, color: HOLOLAB_COLORS.precisionBlue, padding: '2px 8px', borderRadius: '12px', background: 'rgba(46,92,138,0.07)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Zn + H₂SO₄ → ZnSO₄ + H₂
          </span>
        </div>

        <button
          onClick={handleOpenTutorial}
          aria-label="Help"
          style={{ width: '30px', height: '30px', borderRadius: '50%', border: '1px solid rgba(10,25,41,0.12)', background: 'transparent', color: HOLOLAB_COLORS.precisionBlue, fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 150ms' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(46,92,138,0.06)'; e.currentTarget.style.borderColor = HOLOLAB_COLORS.precisionBlue; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(10,25,41,0.12)'; }}
        >
          ?
        </button>

        {/* Progress bar under header */}
        <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: '2px', background: 'rgba(10,25,41,0.06)' }}>
          <div style={{ height: '100%', width: `${overallProgress}%`, background: state.experimentComplete ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.precisionBlue, transition: 'width 300ms ease-out, background 300ms', borderRadius: '0 1px 1px 0' }} />
        </div>
      </header>

      {/* ── Main Content: Split Panel ──────────────────────────────────── */}
      <main style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* LEFT: 3D Scene */}
        <div style={{ flex: '1 1 55%', position: 'relative', minWidth: 0 }}>
          <ExperimentScene3D
            currentStep={state.currentStep}
            isReacting={state.isReacting}
            experimentTime={state.experimentTime}
            buretteReading={state.buretteReading}
            reactionProgress={reactionProgress}
            isPaused={state.isPaused}
          />

          {/* Simulation playback controls */}
          {state.currentStep >= 4 && (
            <SimulationControls
              isPaused={state.isPaused}
              playbackSpeed={state.playbackSpeed}
              experimentTime={state.experimentTime}
              maxTime={MAX_EXPERIMENT_TIME}
              isReacting={state.isReacting}
              experimentComplete={state.experimentComplete}
              onPauseResume={handlePauseResume}
              onSpeedChange={handleSpeedChange}
              onSeek={handleSeek}
            />
          )}

          {/* Observation HUD overlay */}
          {observation && (
            <div style={{ position: 'absolute', bottom: '14px', left: '14px', right: '14px', display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(10,25,41,0.08)', boxShadow: '0 2px 8px rgba(10,25,41,0.06)' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: state.isReacting ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.precisionBlue, marginTop: '5px', flexShrink: 0, boxShadow: state.isReacting ? '0 0 6px rgba(16,185,129,0.4)' : 'none' }} />
              <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', color: 'rgba(10,25,41,0.7)', lineHeight: 1.5 }}>
                {observation}
              </span>
            </div>
          )}

          {/* Drag hint — top right */}
          <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(10,25,41,0.06)', pointerEvents: 'none' }}>
            <span style={{ fontSize: '10px', opacity: 0.5 }}>🖱</span>
            <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', color: 'rgba(10,25,41,0.4)' }}>Drag to rotate</span>
          </div>
        </div>

        {/* RIGHT: Tabbed Panel */}
        <div style={{ flex: '0 0 45%', maxWidth: '520px', display: 'flex', flexDirection: 'column', background: HOLOLAB_COLORS.labWhite, borderLeft: '1px solid rgba(10,25,41,0.06)', overflow: 'hidden' }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(10,25,41,0.06)', flexShrink: 0, background: '#fafcfd' }}>
            {(['procedure', 'data'] as ActiveTab[]).map((tab) => {
              const isActive = activeTab === tab;
              const label = tab === 'procedure' ? 'Procedure' : 'Data & Analysis';
              const icon = tab === 'procedure' ? '🧪' : '📊';
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '10px 0', border: 'none', borderBottom: isActive ? `2px solid ${HOLOLAB_COLORS.precisionBlue}` : '2px solid transparent',
                    background: 'transparent', cursor: 'pointer', transition: 'all 200ms',
                    fontFamily: HOLOLAB_FONTS.body, fontSize: '13px', fontWeight: isActive ? 600 : 500,
                    color: isActive ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.45)',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'rgba(10,25,41,0.7)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'rgba(10,25,41,0.45)'; }}
                >
                  <span style={{ fontSize: '13px' }}>{icon}</span>
                  {label}
                  {tab === 'data' && state.isReacting && (
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: HOLOLAB_COLORS.warningAmber, animation: 'pulse 1.5s infinite' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 18px' }}>
            {activeTab === 'procedure' ? (
              <ExperimentProcedure
                currentStep={state.currentStep}
                isReacting={state.isReacting}
                isPaused={state.isPaused}
                experimentTime={state.experimentTime}
                buretteReading={state.buretteReading}
                observation={observation}
                onStep={handleStep}
                onRecordReading={handleRecordReading}
              />
            ) : (
              <ExperimentTable
                recordedData={state.recordedData}
                highlightTime={state.highlightTime}
                experimentComplete={state.experimentComplete}
                onReset={handleReset}
                autoFillData={experimentConfig.autoFillData}
                showDiscussion={!!experimentConfig.postLab?.enabled}
                discussionQuestions={discussionQuestions?.map((q: any) => ({ _id: q._id, question: q.question, modelAnswer: q.modelAnswer }))}
                onSaveLabData={async (dataPoints) => {
                  if (user && experimentConfig._id) {
                    await saveLabData({ studentId: user._id, experimentConfigId: experimentConfig._id, dataPoints });
                  }
                }}
                onSubmitDiscussion={async (answers) => {
                  if (user && experimentConfig._id) {
                    await submitDiscussionAnswers({ studentId: user._id, experimentConfigId: experimentConfig._id, answers: answers as any });
                  }
                }}
              />
            )}
          </div>
        </div>
      </main>

      {/* Global styles */}
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(10,25,41,0.12); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(10,25,41,0.25); }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
};

export default function GuidedExperimentZnH2SO4Wrapper() {
  const [searchParams] = useSearchParams();
  const idStr = searchParams.get('id');
  const id = idStr ? (idStr as Id<"experimentConfigs">) : undefined;
  
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  
  // Data hooks
  const experimentConfig = useExperiment(id);
  const progress = useStudentProgress(user?._id, id);
  const preLabQuestions = usePreLabQuestions(id);
  const discussionQuestions = useDiscussionQuestions(id);
  
  const submitPreLab = useSubmitPreLabAssessment();
  const teacherBypass = useTeacherBypass();
  // We can add useSaveLabData later if we want to auto-save
  
  if (!id || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-xl text-gray-700">Missing experiment ID or User</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back</button>
      </div>
    );
  }

  if (experimentConfig === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-xl text-red-600">Experiment not found</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back</button>
      </div>
    );
  }

  if (experimentConfig === undefined || progress === undefined || (experimentConfig.preLab.enabled && preLabQuestions === undefined)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Add the logic to show the PreLab if it is enabled and not yet passed
  const needsPreLab = experimentConfig.preLab.enabled && progress !== null && 
                      (progress.status === 'not-started' || progress.status === 'pre-lab') &&
                      (!progress.preLab || !progress.preLab.passed);

  if (needsPreLab && preLabQuestions && preLabQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <button 
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 flex flex-row gap-2 items-center text-gray-500 hover:text-indigo-600"
        >
          <ArrowLeft className="w-5 h-5"/> Back to Dashboard
        </button>
        <PreLabAssessment 
          questions={preLabQuestions.map((q: any) => ({ ...q, id: q._id as string }))}
          passingScore={experimentConfig.preLab.passingScore}
          maxAttempts={experimentConfig.preLab.maxAttempts}
          showAnswersAfterFail={experimentConfig.preLab.showAnswersAfterFail}
          allowTeacherBypass={experimentConfig.allowTeacherBypass}
          onPass={async (answers) => {
            try {
              await submitPreLab({
                experimentConfigId: id,
                studentId: user._id,
                answers: answers.map((a: any) => ({
                  questionId: a.questionId as Id<"preLabQuestions">,
                  answer: a.answer
                }))
              });
            } catch (e: any) {
              alert(e.message || "Failed to save pre-lab progress.");
            }
          }}
          onBypass={async (pwd) => {
            try {
              await teacherBypass({ experimentConfigId: id, studentId: user._id });
            } catch (e: any) {
              alert(e.message || "Invalid password");
            }
          }}
        />
      </div>
    );
  }

  return <ZnH2SO4LabBase experimentConfig={experimentConfig} progress={progress} discussionQuestions={discussionQuestions} />;
}
